import { FilterInput } from '@/components/elements/filter-input/filter-input.component';
import { pretailoringPageMessages } from '../../../pages/products/pre-tailoring/pre-tailoring.messages';

import { commonMessages } from '@/__scripts/common.messages';
import InfoIcon from '@/assets/icons/info.svg?react';
import { SearchInput } from '@/components/elements/search-input/search-input.component';
import { T } from '@/components/i18n/message.i18n.component';
import Button from '@/components/ui/button/button.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Progress } from '@/components/ui/progress/progress.component';
import { LifecycleStatus, Product, Template, TemplatePublicationStatus } from '@/gql/graphql';
import { truncateStringLargerThan } from '@/lib/utils';
import { useDialogStore } from '@/store/dialog.store';
import { SavedAt } from '../saved-at/saved-at.component';
import { ProductInfoModal } from '../tailoring-heading/product-info-modal/product-info-modal.component';
import { tailoringHeadingMessages } from '../tailoring-heading/tailoring-heading.messages';
import { TemplateInfoModal } from '../tailoring-heading/template-info-modal/template-info-modal.component';

export type QuestionFilter = 'ALL' | 'ANSWERED' | 'UNANSWERED';

const filterOptions = [
  { value: 'ALL', label: <T {...pretailoringPageMessages.questionFilterOptionAll} />, testId: 'filter-bar-option-all' },
  {
    value: 'ANSWERED',
    label: <T {...pretailoringPageMessages.questionFilterOptionAnswered} />,
    testId: 'filter-bar-option-answered',
  },
  {
    value: 'UNANSWERED',
    label: <T {...pretailoringPageMessages.questionsFilterOptionUnanswered} />,
    testId: 'filter-bar-option-unanswered',
  },
];

type Props = {
  context: 'pre-tailoring' | 'tailoring';
  id: string;
  name: string;
  search: string;
  status?: string;
  progress: number;
  filter: QuestionFilter;
  data?: Product | Template;
  onFilterSearchChange: (search: string, filter: QuestionFilter) => void;
};

export const QuestionsHeading = ({
  name,
  context,
  id,
  progress,
  search,
  filter,
  onFilterSearchChange,
  status,
  data,
}: Props) => {
  const { openDialog } = useDialogStore((state) => ({
    loading: state.loading,
    openDialog: state.openDialog,
    closeDialog: state.closeDialog,
    setDialogLoading: state.setLoading,
  }));

  const handleShowMoreInfo = () => {
    if (data?.__typename === 'Template') {
      openDialog({
        type: 'custom',
        fullScreen: false,
        title: <T {...tailoringHeadingMessages.templateInfoModalTitle} />,
        element: <TemplateInfoModal template={data as Template} context={context} />,
      });
    }
    if (data?.__typename === 'Product') {
      openDialog({
        type: 'custom',
        fullScreen: false,
        title: <T {...tailoringHeadingMessages.productInfoModalTitle} />,
        element: <ProductInfoModal product={data as Product} context={context} />,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterSearchChange(e.target.value, filter);
  };

  const handleFilterChange = (value: string) => {
    onFilterSearchChange(search, value as QuestionFilter);
  };

  return (
    <div className="flex flex-col">
      <Progress className="w-full rounded-t mb-6" value={progress} />
      <div className="px-7">
        <div className="flex flex-row justify-between items-center mb-4">
          {data?.__typename === 'Product' && (
            <div className="flex flex-row items-center gap-x-2">
              <Button onClick={handleShowMoreInfo} className="flex flex-row gap-x-2" variant="ghost">
                <h2 title={name} data-testid="pre-tailoring-questions-heading-title">
                  {truncateStringLargerThan(name, 40)}
                </h2>
                <InfoIcon />
              </Button>
              {status && (
                <Pill
                  data-testid={`${context}-heading-product-lifecycle-status`}
                  text={status}
                  productStatus={status as LifecycleStatus}
                />
              )}
            </div>
          )}
          {data?.__typename === 'Template' && (
            <div className="flex flex-row items-center gap-x-2">
              <Button onClick={handleShowMoreInfo} className="flex flex-row gap-x-2" variant="ghost">
                <h2 data-testid="pre-tailoring-questions-heading-title">{truncateStringLargerThan(name, 40)}</h2>
                <InfoIcon />
              </Button>
              {status && (
                <Pill
                  data-testid={`${context}-heading-template-publication-status`}
                  text={status}
                  templateStatus={status as TemplatePublicationStatus}
                />
              )}
            </div>
          )}
          <SavedAt data-testid={`${context}-heading-product-saved-at`} productId={id} />
        </div>
        <div className="flex justify-between items-center mb-6">
          <SearchInput
            aria-label="Search questions"
            data-testid="pre-tailoring-questions-heading-search"
            placeholder="Search"
            onSearch={handleSearchChange}
          />
          <div className="flex flex-row items-center">
            <p className="mr-2 typo-secondary">
              <T {...pretailoringPageMessages.questionFilterLabel} />
            </p>
            <FilterInput
              aria-label="Filter questions"
              value={filter}
              options={filterOptions}
              data-testid="pre-tailoring-questions-heading-filter"
              onFilterChange={handleFilterChange}
            />
          </div>
        </div>
        <div className="flex flex-row justify-between items-center mb-3">
          <p className="typo-caption-600 uppercase text-secondary-text">
            <T {...pretailoringPageMessages.title} />
          </p>
          <div data-testid="legend-container" className="flex flex-row items-center gap-x-4">
            <div className="flex flex-row items-center gap-x-2">
              <div className="h-2 w-2 bg-pill-do rounded-full"></div>
              <p className="typo-secondary">
                <T {...commonMessages.designObligation} />
              </p>
            </div>
            <div className="flex flex-row items-center gap-x-2">
              <div className="h-2 w-2 bg-pill-bb rounded-full"></div>
              <p className="typo-secondary">
                <T {...commonMessages.buildingBlock} />
              </p>
            </div>

            <div className="flex flex-row items-center gap-x-2">
              <div className="h-2 w-2 bg-pill-ms rounded-full"></div>
              <p className="typo-secondary">
                <T {...commonMessages.maturityStage} />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
